import { Box, Divider, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Stethoscope } from 'lucide-react';
import { EncounterFieldSchema } from '../../lib/api/modules/specialty';

interface DynamicEncounterFieldsProps {
  /** Section label from the resolved EncounterTemplate, e.g. "Antenatal Check-up". */
  label: string;
  vitalsExtraFields: EncounterFieldSchema[];
  noteExtraFields: EncounterFieldSchema[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
}

/**
 * Renders the extra vitals/note fields for the current department+visitType's EncounterTemplate,
 * if one was resolved. Callers that find no template simply never render this component — today's
 * fixed-field form stays byte-for-byte unchanged.
 */
export function DynamicEncounterFields({
  label,
  vitalsExtraFields,
  noteExtraFields,
  values,
  onChange,
  disabled,
}: DynamicEncounterFieldsProps) {
  if (vitalsExtraFields.length === 0 && noteExtraFields.length === 0) return null;

  const renderField = (field: EncounterFieldSchema) => (
    <Grid item xs={12} sm={6} key={field.key}>
      {field.type === 'select' ? (
        <TextField
          fullWidth select size="small" label={field.label} disabled={disabled}
          value={values[field.key] ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        >
          {(field.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          fullWidth size="small" label={field.label} disabled={disabled}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
          value={values[field.key] ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          InputProps={field.unit ? { endAdornment: field.unit } : undefined}
        />
      )}
    </Grid>
  );

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}
      >
        <Stethoscope size={16} /> {label}
      </Typography>
      {vitalsExtraFields.length > 0 && (
        <Grid container spacing={2} sx={{ mb: noteExtraFields.length > 0 ? 2 : 0 }}>
          {vitalsExtraFields.map(renderField)}
        </Grid>
      )}
      {noteExtraFields.length > 0 && (
        <Grid container spacing={2}>
          {noteExtraFields.map(renderField)}
        </Grid>
      )}
    </Box>
  );
}

/** Serializes {key: value} answers to the JSON string the backend's *ExtraJson/structuredDataJson columns store. */
export function encodeEncounterFieldValues(values: Record<string, string>): string | undefined {
  const nonEmpty = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '' && v != null));
  return Object.keys(nonEmpty).length > 0 ? JSON.stringify(nonEmpty) : undefined;
}
